import { Clapperboard, Loader2, AlertCircle, Download, RotateCcw, Sparkles } from 'lucide-react';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';

export const PresenterRenderPanel = ({ video, onUpdate, onRefresh }) => {
  const ready = !!video.avatar?.image_path && !!video.audio_path && video.audio_status === 'ready';
  const rendering = video.status === 'rendering';

  const render = async () => {
    try { await api.post(`/presenter/${video.id}/render`); await onRefresh(); toast.success('Render started — this takes a few minutes'); }
    catch (e) { toast.error(e.response?.data?.detail?.split('|').pop() || 'Could not start render'); }
  };
  const reset = async () => { try { await api.post(`/presenter/${video.id}/reset`); await onRefresh(); } catch { toast.error('Reset failed'); } };

  return (
    <section className="ff-card rounded-2xl p-5" data-testid="presenter-render-panel">
      <h2 className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#E879F9] flex items-center gap-2 mb-3"><Clapperboard className="w-3.5 h-3.5" /> 4 · Render</h2>

      <label className="block text-[10px] uppercase tracking-wider text-[#8A8A93] mb-1.5">Performance direction</label>
      <textarea data-testid="performance-prompt" defaultValue={video.performance_prompt || ''} rows={2} onBlur={e => onUpdate({ performance_prompt: e.target.value }, { silent: true })}
        className="ff-input w-full rounded-lg px-3 py-2 text-xs resize-none" placeholder="Gestures, emotion, camera (e.g. slow push-in, warm smile at the end)" />

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <div className="flex gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06]">
          {[{ id: 'std', label: 'Standard' }, { id: 'pro', label: 'Professional' }].map(m => (
            <button key={m.id} data-testid={`render-mode-${m.id}`} onClick={() => onUpdate({ mode: m.id }, { silent: true })}
              className={`px-3 py-1 rounded-md text-[11px] font-medium transition-colors ${(video.mode || 'std') === m.id ? 'bg-[#E879F9]/15 text-white' : 'text-[#8A8A93] hover:text-white'}`}>{m.label}</button>
          ))}
        </div>
        <span className="text-[10px] text-[#8A8A93]">Billed by Kling per second of audio{video.audio_duration ? ` · ${Math.round(video.audio_duration)}s` : ''}</span>
        <button data-testid="render-btn" onClick={render} disabled={!ready || rendering} className="ff-btn-primary text-xs flex items-center gap-2 disabled:opacity-50 ml-auto">
          {rendering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {video.video_path ? 'Render again' : 'Render video'}
        </button>
      </div>
      {!ready && !rendering && <p className="text-[11px] text-[#8A8A93] mt-2">Needs an avatar image and a ready voice track.</p>}

      {rendering && (
        <div className="mt-3 p-3 rounded-lg bg-[#E879F9]/5 border border-[#E879F9]/20 flex items-center gap-2" data-testid="render-progress">
          <Loader2 className="w-4 h-4 text-[#E879F9] animate-spin shrink-0" />
          <p className="text-xs text-[#E879F9]">{video.progress || 'Rendering…'}</p>
          <button onClick={reset} className="ml-auto text-[10px] text-[#8A8A93] hover:text-white flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Reset</button>
        </div>
      )}
      {video.status === 'failed' && (
        <div className="mt-3 p-3 rounded-lg bg-red-500/5 border border-red-500/20 flex items-start gap-2" data-testid="render-error">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <div><p className="text-xs text-red-400 font-medium">Render failed</p><p className="text-[11px] text-red-300/80 mt-0.5 break-words">{video.error}</p></div>
        </div>
      )}
      {video.video_path && (
        <div className="mt-4" data-testid="render-result">
          <video controls src={getImageUrl(video.video_path)} className="w-full rounded-xl bg-black max-h-[420px]" data-testid="render-video" />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px] text-[#8A8A93]">{video.takes?.length > 1 ? `${video.takes.length} takes` : 'Latest take'}{video.kling_units ? ` · ${video.kling_units} Kling units` : ''}</span>
            <a data-testid="render-download" href={getImageUrl(video.video_path)} download={`${video.title || 'presenter'}.mp4`} className="ff-btn-secondary text-xs flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Download MP4</a>
          </div>
        </div>
      )}
    </section>
  );
};
